import { useMemo, useState, useEffect, useCallback, Fragment, MouseEvent, ReactNode } from 'react'
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom'
import { AppBar, MenuItem } from '@mui/material'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import { UserRole } from '@common/interfaces/user'
import classnames from 'classnames'
import Logo from '../../assets/images/client-intake/dwight-black-logo.svg?react'
import GlobalSearch from '../../components/GlobalSearch'
import config from '../../config'
import { ROUTES, isBlankLayoutRoute } from '../../constants/routes'
import { setBaseTitle } from '../../helpers/documentTitle'
import { useRouteTitle } from '../../hooks/useRouteTitle'
import ColorModeToggle from '../ColorModeToggle'
import Avatar from '../Common/Avatar'
import Icon from '../Common/Icon'
import Menu from '../Common/Menu'
import MessageModal from '../Common/MessageModal'
import Text from '../Common/Text'
import ContextSwitcherItems, { IContextOption } from '../ContextSwitcherItems'
import styles from './HeaderClient.module.scss'
import { PropsFromRedux } from './index'
import { titles } from './titles'

const PAGE_ICONS: Record<string, ReactNode> = {
  [ROUTES.HOMEPAGE]: <Icon name="sidemenu-overview" size="sm" />,
  [ROUTES.ACCOUNT_ACTIVITY]: <Icon name="sidemenu-activity" size="sm" />,
  [ROUTES.FUNDING]: <Icon name="sidemenu-funding" size="sm" />,
  [ROUTES.SUBMIT_REPORTING]: <Icon name="sidemenu-reporting" size="sm" />,
  [ROUTES.CLIENT_VISUALIZATIONS]: <Icon name="sidemenu-analytics" size="sm" />,
  [ROUTES.APPLICATION_PAGE]: <Icon name="sidemenu-funding" size="sm" />,
  [ROUTES.DOCUMENTS_PAGE]: <Icon name="sidemenu-activity" size="sm" />,
}

const HeaderUserActions = ({
  anchorEl,
  handleCloseMenu,
  handleLogout,
  handleViewProfile,
  handleContactRM,
  isDiligenceMode,
  isProspectMode,
  companyOptions,
  currentCompanyId,
  onSwitchCompany,
}: {
  anchorEl: HTMLElement | null
  handleCloseMenu: () => void
  handleLogout: () => void
  handleViewProfile: () => void
  handleContactRM: () => void
  isDiligenceMode: boolean
  isProspectMode: boolean
  companyOptions: IContextOption[]
  currentCompanyId?: string
  onSwitchCompany: (clientId: string) => void
}) => {
  return (
    <Menu
      controlledAnchorEl={anchorEl}
      onClose={handleCloseMenu}
      paperClassName={styles.actionsMenu}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
    >
      <ContextSwitcherItems
        title="Switch company"
        options={companyOptions}
        currentValue={currentCompanyId}
        itemClassName={styles.actionsMenuItem}
        onSelect={onSwitchCompany}
      />
      <Text variant="title" tone="secondary" component="div" className={styles.menuHeader}>
        Account menu
      </Text>
      <MenuItem classes={{ root: styles.actionsMenuItem }} onClick={handleViewProfile}>
        <Icon name="user" size="md" />
        View profile
      </MenuItem>
      {!isProspectMode && (
        <MenuItem classes={{ root: styles.actionsMenuItem }} onClick={handleContactRM}>
          <Icon name="message-text" size="md" />
          {isDiligenceMode ? 'Contact us' : 'Contact RM'}
        </MenuItem>
      )}
      <MenuItem classes={{ root: styles.actionsMenuItem }} onClick={handleLogout}>
        <Icon name="logout-alt" size="md" />
        Sign out
      </MenuItem>
    </Menu>
  )
}
interface IProps extends PropsFromRedux {
  workflow: string
  /** Owned by MainLayout, which decides whether the side menu renders at all. */
  hasSideMenu?: boolean
}

const HeaderClient = ({
  user,
  role,
  isSidebarOpen,
  title,
  onLogout,
  isAdminLoggedAsClient,
  clientInfo,
  sendMessage,
  isDueDiligenceClient,
  isIntakeProspectClient,
  clientMemberships,
  switchClient,
  hasSideMenu = true,
}: IProps) => {
  const [pageTitle, setPageTitle] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const { pathname } = useLocation()
  const navigate = useNavigate()

  const routeTitle = useRouteTitle(titles)

  useEffect(() => {
    if (routeTitle) {
      setPageTitle(routeTitle)
      setBaseTitle(routeTitle)
    } else {
      setPageTitle(title)
    }
  }, [routeTitle, title])

  const isNotesAndSearchUser = useMemo(() => ![UserRole.CLIENT_USER].includes(role), [role])

  const [isMessageOpen, setIsMessageOpen] = useState(false)

  const handleLogout = useCallback(async () => {
    await onLogout()
  }, [onLogout])

  const handleClickMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleViewProfile = useCallback(() => {
    handleCloseMenu()
    void navigate(ROUTES.SETTINGS)
  }, [handleCloseMenu, navigate])

  const handleContactRM = useCallback(() => {
    handleCloseMenu()
    setIsMessageOpen(true)
  }, [handleCloseMenu])

  const handleSendMessage = useCallback(
    async (values: { subject: string; message: string }) => {
      const recipientEmail = clientInfo?.primaryManager?.email || config.defaultOpsEmail
      await sendMessage({
        subject: values.subject,
        message: values.message,
        recipientEmail,
      })
    },
    [sendMessage, clientInfo],
  )

  const companyOptions = useMemo(
    () =>
      isAdminLoggedAsClient
        ? []
        : clientMemberships.map(({ clientId, clientName }) => ({
            value: clientId,
            label: clientName,
          })),
    [clientMemberships, isAdminLoggedAsClient],
  )

  const handleSwitchCompany = useCallback(
    (clientId: string) => {
      handleCloseMenu()
      void switchClient(clientId)
    },
    [handleCloseMenu, switchClient],
  )

  const userName = useMemo(
    () => (isAdminLoggedAsClient ? clientInfo?.clientName || '' : user?.firstName),
    [isAdminLoggedAsClient, user, clientInfo],
  )

  const isBlankLayout = useMemo(() => isBlankLayoutRoute(pathname), [pathname])
  if (!user) {
    return null
  }
  return (
    <AppBar
      className={classnames(styles.appBar, {
        [styles.loggedAsClientAppBar]: isAdminLoggedAsClient,
        [styles.appBarExpanded]: isSidebarOpen && hasSideMenu,
        [styles.appBarFullWidth]: !hasSideMenu,
      })}
    >
      <Grid container className={styles.appBarContainer} size={12}>
        <Grid size={4}>
          <div className={styles.leftSlot}>
            {!hasSideMenu && (
              <RouterLink
                to={ROUTES.HOMEPAGE}
                className={styles.headerLogo}
                aria-label="Dwight home"
              >
                <Logo />
              </RouterLink>
            )}
            <div
              className={classnames(styles.titleContainer, {
                [styles.titleExpanded]: isSidebarOpen && hasSideMenu,
              })}
              id="pageTitle"
            >
              {PAGE_ICONS[pathname] && (
                <span className={styles.titleIcon}>{PAGE_ICONS[pathname]}</span>
              )}
              <span className={styles.title}>{pageTitle}</span>
            </div>
          </div>
        </Grid>
        <Grid className={styles.centerSlot} size={4}>
          {!isBlankLayout && isNotesAndSearchUser && (
            <div className={styles.searchBar}>
              <GlobalSearch />
            </div>
          )}
        </Grid>

        <Grid className={styles.rightSlot} size={4}>
          <ColorModeToggle />
          {user && (
            <Fragment>
              <div className={styles.user} onClick={handleClickMenu}>
                {!isAdminLoggedAsClient && (
                  <div className={styles.headerIcons}>
                    <Link
                      className={styles.actionsMenuItemLink}
                      component={RouterLink}
                      to={ROUTES.SETTINGS}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Icon name="settings" size="md" className={styles.headerIcon} />
                    </Link>
                  </div>
                )}

                <Avatar user={user} clientInfo={isAdminLoggedAsClient ? clientInfo : null} />

                <div className={styles.userInfo}>
                  <span data-cy="header-username" className={styles.userName}>
                    {userName}
                  </span>
                  {companyOptions.length > 1 && (
                    <span className={styles.userCompany}>{clientInfo?.clientName}</span>
                  )}
                </div>
              </div>

              <HeaderUserActions
                anchorEl={anchorEl}
                handleCloseMenu={handleCloseMenu}
                handleLogout={handleLogout}
                handleViewProfile={handleViewProfile}
                handleContactRM={handleContactRM}
                isDiligenceMode={isDueDiligenceClient}
                isProspectMode={Boolean(isIntakeProspectClient)}
                companyOptions={companyOptions}
                currentCompanyId={clientInfo?.id}
                onSwitchCompany={handleSwitchCompany}
              />
            </Fragment>
          )}
        </Grid>
      </Grid>

      <MessageModal
        isOpen={isMessageOpen}
        onClose={() => setIsMessageOpen(false)}
        onSend={handleSendMessage}
        managerEmail={clientInfo?.primaryManager?.email}
        managerName={
          clientInfo?.primaryManager
            ? `${clientInfo.primaryManager.firstName} ${clientInfo.primaryManager.lastName}`
            : undefined
        }
        // Already resolved server-side (uploaded photo → legacy column → IdP),
        // so preferring googleAvatarUrl here would have hidden an upload.
        managerAvatar={clientInfo?.primaryManager?.avatar}
        isDiligenceMode={isDueDiligenceClient}
      />
    </AppBar>
  )
}

export default HeaderClient
